import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch } from 'react-redux';
import { DefaultErrorBoundary } from '@mews-commander/ui';
import {
    Avatar,
    Link,
    Stack,
    Table,
    TableActiveColumnFilters,
    TableHeader,
    TableProvider,
    usePaginatedData,
    useTableState,
} from '@optimus-web/core';
import { useAppConfiguration } from '@mews-commander/data-access';
import { TextKey, RouteName } from '@mews-commander/enums';
import { lt, RouterService } from '@mews/framework';
import { ImageResizeMode } from '@mews/enums';
import { useResolveRoute } from '@mews/routing';
import { ExtendedGeneralEnterprise, useGetFilteredGeneralEnterprises } from '../actions';
import { navigateInBaseModal } from '../../app/actions/common';

const columnHelper = createColumnHelper<ExtendedGeneralEnterprise>();

const useColumns = () => {
    const appConfiguration = useAppConfiguration();
    const resolveRoute = useResolveRoute();
    const dispatch = useDispatch();

    return [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => lt(TextKey.Name),
            cell: (props) => {
                const enterpriseName = props.getValue();
                const url = resolveRoute(RouteName.GuestExperienceWhitelistedUserEmails, {
                    query: { OwnerId: props.row.original.id },
                });
                const src = props.row.original.logoImageId
                    ? RouterService.imageUrl(props.row.original.logoImageId, appConfiguration.imageBaseUrl, {
                          mode: ImageResizeMode.FitExact,
                          width: 64,
                          height: 64,
                      })
                    : '';
                return (
                    <Link
                        inherit
                        onClick={() => {
                            dispatch(navigateInBaseModal(url));
                        }}
                    >
                        <Stack spacing="300" itemAlignment="center" alignment="center">
                            <Avatar title={enterpriseName} src={src} size="medium" />
                            {enterpriseName}
                        </Stack>
                    </Link>
                );
            },
        }),
        columnHelper.accessor('city', {
            id: 'city',
            header: () => lt(TextKey.City),
            cell: (props) => props.getValue(),
        }),
        columnHelper.accessor('country', {
            id: 'country',
            header: () => lt(TextKey.Country),
            cell: (props) => props.getValue(),
        }),
    ];
};

export const EnterpriseListTable = () => {
    const [tableState, setTableState] = useTableState<ExtendedGeneralEnterprise>();

    const { isLoading, data } = useGetFilteredGeneralEnterprises({ tableState });
    const { data: paginatedData, totalRowCount } = usePaginatedData(data?.enterprises ?? [], tableState.pagination);
    const columns = useColumns();

    return (
        <DefaultErrorBoundary>
            <TableProvider state={tableState} onStateChange={setTableState} columns={columns}>
                <TableHeader title={lt(TextKey.Properties)} globalSearch subTitle={data?.enterprises.length ?? 0} />
                <TableActiveColumnFilters />
                <Table
                    uniqueRowId="id"
                    data={paginatedData}
                    loading={isLoading}
                    amountOfRows={totalRowCount}
                    emptyState={{
                        primaryText: lt(TextKey.WhitelistedUserEmailsEnterprisesTableNoData),
                    }}
                />
            </TableProvider>
        </DefaultErrorBoundary>
    );
};
